import { getDb, META_KEYS, readMeta, writeMeta, type SmartkeurDatabase } from '../offline/db';
import { describeDevice, detectPlatform, type DevicePlatform } from './device-platform';
import { encodeRandom, isUlid, ulid } from './ulid';

/**
 * Apparaat-identiteit
 * ===================
 * Elk apparaat krijgt één keer een ULID, bewaard in de `meta`-tabel onder
 * `META_KEYS.deviceId`. Hij overleeft een herstart, uitloggen en een andere
 * gebruiker; alleen het wissen van de sitegegevens maakt een nieuwe.
 *
 * Stond daar nog een oudere UUID (van vóór de API ULID's eiste), dan wordt die
 * één keer vervangen: de API weigert elke andere vorm met een 422.
 */
const loading = new WeakMap<SmartkeurDatabase, Promise<string>>();

export function getDeviceUuid(db: SmartkeurDatabase = getDb()): Promise<string> {
    const existing = loading.get(db);

    if (existing) {
        return existing;
    }

    const created = loadOrCreate(db);

    loading.set(db, created);
    created.catch(() => loading.delete(db));

    return created;
}

async function loadOrCreate(db: SmartkeurDatabase): Promise<string> {
    return db.transaction('rw', db.meta, async () => {
        const stored = await readMeta<unknown>(db, META_KEYS.deviceId);

        if (isUlid(stored)) {
            return stored;
        }

        const created = ulid();

        await writeMeta(db, META_KEYS.deviceId, created);

        return created;
    });
}

/**
 * De ULID waarmee dit apparaat zich voor één persoon bij de API aanmeldt.
 *
 * De API koppelt een apparaat aan de persoon die het registreerde: zoekt hij
 * een apparaat op, dan op ULID *én* gebruiker, en de ULID zelf is uniek. Logt
 * een tweede persoon op dezelfde tablet of in dezelfde browser in met dezelfde
 * ULID, dan weigert de API (zie `RegisterDevice` in de API). Daarom krijgt elke
 * persoon op dit apparaat een eigen, vaste afleiding van de apparaat-ULID:
 *
 * - dezelfde persoon op hetzelfde apparaat krijgt altijd dezelfde waarde, dus
 *   opnieuw inloggen werkt de bestaande registratie bij in plaats van er een
 *   bij te maken;
 * - twee personen op hetzelfde apparaat krijgen elk een eigen registratie;
 * - de tijd vooraan blijft die van het apparaat.
 *
 * Er wordt niets extra's opgeslagen: de afleiding is een functie van de
 * bewaarde apparaat-ULID en het e-mailadres. Die waarde is geen geheim - de API
 * staat niet toe dat iemand met alleen een ULID andermans apparaat overneemt.
 */
export async function deviceUuidFor(email: string, db?: SmartkeurDatabase): Promise<string> {
    return deriveDeviceUuid(await getDeviceUuid(db), email);
}

/** Het `device`-blok van `POST /auth/login` en van een geaccepteerde uitnodiging. */
export interface DeviceRegistration {
    uuid: string;
    name: string;
    platform: DevicePlatform;
}

export async function deviceRegistrationFor(
    email: string,
    db?: SmartkeurDatabase,
): Promise<DeviceRegistration> {
    return {
        uuid: await deviceUuidFor(email, db),
        name: describeDevice(),
        platform: detectPlatform(),
    };
}

export function deriveDeviceUuid(deviceUuid: string, email: string): string {
    const person = email.trim().toLowerCase();

    return deviceUuid.slice(0, 10) + encodeRandom(digest80(`${deviceUuid}:${person}`));
}

const MASK_64 = (1n << 64n) - 1n;

/** 80 bits uit twee FNV-1a-rondes met een murmur-finalizer. */
function digest80(input: string): Uint8Array {
    const first = mix(fnv1a(input, 0n));
    const second = mix(fnv1a(input, first));
    const bytes = new Uint8Array(10);

    for (let index = 0; index < 8; index += 1) {
        bytes[index] = Number((first >> BigInt(56 - index * 8)) & 0xffn);
    }

    bytes[8] = Number((second >> 8n) & 0xffn);
    bytes[9] = Number(second & 0xffn);

    return bytes;
}

function fnv1a(input: string, seed: bigint): bigint {
    let hash = 0xcbf29ce484222325n ^ seed;

    for (let index = 0; index < input.length; index += 1) {
        hash ^= BigInt(input.charCodeAt(index));
        hash = (hash * 0x100000001b3n) & MASK_64;
    }

    return hash;
}

function mix(value: bigint): bigint {
    let hash = value;

    hash ^= hash >> 33n;
    hash = (hash * 0xff51afd7ed558ccdn) & MASK_64;
    hash ^= hash >> 33n;
    hash = (hash * 0xc4ceb9fe1a85ec53n) & MASK_64;
    hash ^= hash >> 33n;

    return hash;
}
